/**
 * Searchable signal chooser, shared by the add-widget dialog and anywhere else
 * that needs to name a channel.
 */
import { useMemo, useState } from 'react';
import type { ObdSession, SignalState } from '@obd/core';

export interface SignalPickerProps {
  session: ObdSession;
  selected: string[];
  onToggle: (key: string) => void;
}

export function SignalPicker({ session, selected, onToggle }: SignalPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    const all = session.store.list();
    // Derived and trip channels are always computable, so they stay listed even
    // before the car has answered for their inputs.
    const base = showAll
      ? all
      : all.filter((s) => s.available || s.def.category === 'derived' || s.def.category === 'trip');
    const q = query.trim().toLowerCase();
    const filtered = q
      ? base.filter(
        (s) =>
          s.def.name.toLowerCase().includes(q) ||
            s.def.short.toLowerCase().includes(q) ||
            s.def.key.includes(q) ||
            s.def.category.includes(q),
      )
      : base;
    return filtered.sort(
      (a, b) => a.def.category.localeCompare(b.def.category) || a.def.name.localeCompare(b.def.name),
    );
  }, [session, query, showAll]);

  const grouped = useMemo(() => {
    const map = new Map<string, SignalState[]>();
    for (const s of rows) {
      const list = map.get(s.def.category);
      if (list) list.push(s);
      else map.set(s.def.category, [s]);
    }
    return [...map.entries()];
  }, [rows]);

  return (
    <div className="picker">
      <div className="picker-toolbar">
        <input
          type="text"
          placeholder="Search signals…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1 }}
        />
        <label className="field-inline">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          <span className="muted">Include unsupported</span>
        </label>
      </div>

      <div className="picker-list">
        {grouped.length === 0 && <p className="muted">No signals match.</p>}
        {grouped.map(([category, items]) => (
          <div key={category}>
            <div className="picker-group">{category}</div>
            {items.map((s) => {
              const isSelected = selected.includes(s.def.key);
              return (
                <button
                  key={s.def.key}
                  className="picker-item"
                  data-selected={isSelected || undefined}
                  onClick={() => onToggle(s.def.key)}
                >
                  <span className="picker-check">{isSelected ? '✓' : ''}</span>
                  <span className="picker-name">{s.def.name}</span>
                  <span className="muted picker-unit">{s.def.unit}</span>
                  {!s.available && <span className="pill">unsupported</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
